import { useState, useEffect } from 'react';
import { chainAPI } from '../lib/api';
import { ChevronRight, ChevronLeft, AlertTriangle, CheckCircle, BookOpen, MapPin, Calendar } from 'lucide-react';

const STEPS = [
  { key: 'origin', label: 'ORIGIN', sublabel: 'The Root', color: '#D4A84A' },
  { key: 'change_a', label: 'CHANGE A', sublabel: 'First Modification', color: '#2F8A92' },
  { key: 'change_b', label: 'CHANGE B', sublabel: 'Second Modification', color: '#2F8A92' },
  { key: 'contradiction', label: 'CONTRADICTION', sublabel: 'The Split', color: '#FF4444' },
  { key: 'modern_pattern', label: 'MODERN PATTERN', sublabel: 'The Consistency', color: '#2F8A92' },
];

const EMPTY_CONTRADICTION = "No major conflicting verified accounts are currently attached to this record. If you have a source that challenges this record, you can submit it for review.";

function StepIndicator({ steps, current, onStepClick }) {
  return (
    <div className="flex items-center gap-1 mb-6" data-testid="chain-step-indicator">
      {steps.map((step, i) => (
        <div key={step.key} className="flex items-center">
          <button
            data-testid={`step-${step.key}`}
            onClick={() => onStepClick(i)}
            className={`w-8 h-8 rounded-full flex items-center justify-center font-hud text-xs font-bold transition-all duration-300
              ${i === current 
                ? `bg-[${step.color}]/20 border-2 text-[${step.color}] cyan-glow`
                : i < current
                  ? 'bg-[#2F8A92]/10 border border-[#2F8A92]/40 text-[#2F8A92]'
                  : 'border border-[#52525B] text-[#52525B]'
              }`}
            style={{ borderColor: i === current ? step.color : undefined, color: i === current ? step.color : undefined }}
          >
            {i + 1}
          </button>
          {i < steps.length - 1 && (
            <div className={`w-8 h-px ${i < current ? 'bg-[#2F8A92]/40' : 'bg-[#52525B]/40'}`} />
          )}
        </div>
      ))}
    </div>
  );
}

function ContradictionView({ chain }) {
  const hasContradiction = chain.has_contradiction && chain.contradiction;
  
  if (!hasContradiction) {
    return (
      <div className="glass-panel p-6 text-center" data-testid="contradiction-empty">
        <AlertTriangle className="mx-auto mb-4 text-[#D4A84A]" size={32} />
        <p className="font-mono text-sm text-white/80 leading-relaxed italic">
          {EMPTY_CONTRADICTION}
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="contradiction-split-view">
      <div className="glass-panel p-5 border-l-2 border-[#2F8A92]">
        <h4 className="font-hud text-[#2F8A92] text-sm tracking-wider uppercase mb-2">SOURCE A</h4>
        <h3 className="font-historical text-[#D4A84A] text-lg mb-3">{chain.contradiction?.title}</h3>
        <p className="font-mono text-sm text-white/80 leading-relaxed mb-3">{chain.contradiction?.content}</p>
        {chain.contradiction?.source && (
          <p className="font-mono text-xs text-[#52525B]">
            <BookOpen className="inline mr-1" size={12} /> {chain.contradiction.source}
          </p>
        )}
      </div>
      <div className="glass-panel p-5 border-l-2 border-[#D4A84A]">
        <h4 className="font-hud text-[#D4A84A] text-sm tracking-wider uppercase mb-2">SOURCE B</h4>
        <h3 className="font-historical text-[#D4A84A] text-lg mb-3">{chain.contradiction_source_b?.title}</h3>
        <p className="font-mono text-sm text-white/80 leading-relaxed mb-3">{chain.contradiction_source_b?.content}</p>
        {chain.contradiction_source_b?.source && (
          <p className="font-mono text-xs text-[#52525B]">
            <BookOpen className="inline mr-1" size={12} /> {chain.contradiction_source_b.source}
          </p>
        )}
      </div>
    </div>
  );
}

export default function TelephoneGame({ onNodeSelect, onChainSelect }) {
  const [chains, setChains] = useState([]);
  const [selectedChain, setSelectedChain] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    chainAPI.list().then(r => { setChains(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const selectChain = (chain) => {
    // If onChainSelect is provided, navigate to ContentPage instead
    if (onChainSelect) {
      onChainSelect(chain);
      return;
    }
    setSelectedChain(chain);
    setCurrentStep(0);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="font-hud text-[#2F8A92] text-sm tracking-wider animate-pulse">LOADING CHAINS...</div>
      </div>
    );
  }

  if (!selectedChain) {
    return (
      <div className="space-y-3 md:space-y-4" data-testid="chain-list">
        {chains.map(chain => (
          <button
            key={chain.id}
            data-testid={`chain-item-${chain.id}`}
            onClick={() => selectChain(chain)}
            className="w-full glass-panel p-5 md:p-6 text-left hover:border-[#2F8A92]/40 transition-all duration-300 group"
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <h3 className="font-historical text-[#D4A84A] text-xl md:text-2xl lg:text-3xl font-light leading-tight transition-all duration-300">
                  {chain.subject}
                </h3>
                <p className="font-mono text-sm md:text-base text-white/75 mt-2 leading-relaxed">{chain.description}</p>
              </div>
              <ChevronRight className="text-[#2F8A92] opacity-0 group-hover:opacity-100 transition-all duration-300 flex-shrink-0" size={22} />
            </div>
            <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-3 font-hud text-[10px] md:text-xs text-white/80 tracking-wider">
              <span className="flex items-center gap-1">
                <MapPin size={11} /> {chain.origin?.location}
              </span>
              {chain.has_contradiction && (
                <span className="text-[#FF4444] flex items-center gap-1">
                  <AlertTriangle size={11} /> CONTRADICTION
                </span>
              )}
            </div>
          </button>
        ))}
        {chains.length === 0 && (
          <div className="text-center py-12">
            <p className="font-mono text-sm md:text-base text-white/65">No chains here yet. Check back soon — we're adding them daily.</p>
          </div>
        )}
      </div>
    );
  }

  const step = STEPS[currentStep];
  const stepData = selectedChain[step.key];
  const isContradiction = step.key === 'contradiction';

  return (
    <div className="p-6 space-y-4" data-testid="telephone-game">
      <button
        data-testid="back-to-chains"
        onClick={() => setSelectedChain(null)}
        className="font-hud text-xs text-white/80 tracking-wider hover:text-[#2F8A92] transition-all duration-300 flex items-center gap-1"
      >
        <ChevronLeft size={14} /> BACK TO CHAINS
      </button>

      <h2 className="font-historical text-[#D4A84A] text-2xl md:text-3xl font-light tracking-tight">
        {selectedChain.subject}
      </h2>

      <StepIndicator steps={STEPS} current={currentStep} onStepClick={setCurrentStep} />

      <div className="font-hud text-xs text-white/80 tracking-widest uppercase mb-2" style={{ color: step.color }}>
        {step.label} — {step.sublabel}
      </div>

      {isContradiction ? (
        <ContradictionView chain={selectedChain} />
      ) : stepData ? (
        <div className="glass-panel p-6 animate-fade-in" data-testid={`step-content-${step.key}`}>
          <h3 className="font-historical text-[#D4A84A] text-xl mb-3">{stepData.title}</h3>
          <p className="font-mono text-sm text-white/80 leading-relaxed mb-4">{stepData.content}</p>
          <div className="flex flex-wrap items-center gap-4 font-hud text-xs text-[#52525B] tracking-wider">
            {stepData.source && (
              <span className="flex items-center gap-1"><BookOpen size={10} /> {stepData.source}</span>
            )}
            {stepData.year && (
              <span className="flex items-center gap-1"><Calendar size={10} /> {stepData.year < 0 ? `${Math.abs(stepData.year)} BCE` : `${stepData.year} CE`}</span>
            )}
            {stepData.location && (
              <button
                data-testid={`locate-${step.key}`}
                onClick={() => onNodeSelect && stepData.lat != null && onNodeSelect({ lat: stepData.lat, lng: stepData.lng })}
                className="flex items-center gap-1 text-[#2F8A92] hover:text-[#66FAFF] transition-all duration-300 cursor-pointer"
              >
                <MapPin size={10} /> {stepData.location}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="glass-panel p-6 text-center">
          <p className="font-mono text-sm text-[#52525B]">No data for this step.</p>
        </div>
      )}

      <div className="flex items-center justify-between pt-4">
        <button
          data-testid="prev-step"
          onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
          disabled={currentStep === 0}
          className="font-hud text-sm tracking-wider text-[#2F8A92] hover:text-[#66FAFF] disabled:text-[#52525B] disabled:cursor-not-allowed transition-all duration-300 flex items-center gap-1"
        >
          <ChevronLeft size={16} /> PREV
        </button>
        <span className="font-hud text-xs text-[#52525B] tracking-wider">
          {currentStep + 1} / {STEPS.length}
        </span>
        <button
          data-testid="next-step"
          onClick={() => setCurrentStep(Math.min(STEPS.length - 1, currentStep + 1))}
          disabled={currentStep === STEPS.length - 1}
          className="font-hud text-sm tracking-wider text-[#2F8A92] hover:text-[#66FAFF] disabled:text-[#52525B] disabled:cursor-not-allowed transition-all duration-300 flex items-center gap-1"
        >
          NEXT <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}
